// src/vlp/components/maintenance/test-process/ProcessTestSettingPopup.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  Button,
  Grid,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { NotificationManager } from "react-notifications";

const ProcessTestSettingPopup = ({
  open,
  onClose,
  onSave,
  editingSetting,
  existingSettings,
  prefilledData, // New prop for prefilled data from test selection
}) => {  
  const [formData, setFormData] = useState({
    vlpTestCode: "",
    processTestCode: "",
    parameter: "",
    processId: "",
    methodCode: "",
    minControlLimit: "",
    maxControlLimit: "",
    costOfAnalysis: "",
    testUnits: "",
    sortOrder: "",
    recordid: 0,
    isLimit: false,
    sampleLocationId: "",
  });
  const [errors, setErrors] = useState({});

  // populate form when editing, using prefilledData, or using existingSettings placeholder
  useEffect(() => {
    if (editingSetting) {
      setFormData(editingSetting);
    } else if (prefilledData) {
      // Use prefilled data from test selection
      setFormData(prefilledData);
    } else if (existingSettings && existingSettings.length > 0) {
      const placeholder = existingSettings[0];
      setFormData((prev) => ({
        ...prev,
        processId: placeholder.processId,
        methodCode: placeholder.methodCode,
        testUnits: placeholder.testUnits,
      }));
    } else {
      // reset to default (keep same shape)
      setFormData({
        vlpTestCode: "",
        processTestCode: "",
        parameter: "",
        processId: "",
        methodCode: "",
        minControlLimit: "",
        maxControlLimit: "",
        costOfAnalysis: "",
        testUnits: "",
        sortOrder: "",
        recordid: 0,
        isLimit: false,
        sampleLocationId: "",
      });
    }
    // Clear errors when form data changes
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingSetting, existingSettings, prefilledData, open]);

  const handleInputChange = (field, value) => {
    // handle boolean strings for Select (in case value comes as "true"/"false")
    if (value === "true") value = true;
    if (value === "false") value = false;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.vlpTestCode)
      newErrors.vlpTestCode = "VLP Test Code is required";
    if (!formData.processTestCode)
      newErrors.processTestCode = "Process Test Code is required";
    if (!formData.parameter) newErrors.parameter = "Parameter is required";
    if (!formData.methodCode) newErrors.methodCode = "Method Code is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const settingData = {
      ...formData,
      costOfAnalysis: formData.costOfAnalysis
        ? parseFloat(formData.costOfAnalysis)
        : null,
      sortOrder: formData.sortOrder ? parseInt(formData.sortOrder, 10) : null,
      sampleLocationId: formData.sampleLocationId
        ? parseInt(formData.sampleLocationId, 10)
        : null,
    };

    onSave(settingData);
    onClose();
  };

  const isPrefilledMode = !!prefilledData;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        bgcolor: isPrefilledMode ? 'primary.lighter' : 'inherit'
      }}>
        {editingSetting ? "Edit Test Setting" : "Add Test Setting"}
        
      </DialogTitle>

      <DialogContent>
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* VLP Test Code - Read only when prefilled */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="VLP Test Code"
              value={formData.vlpTestCode}
              onChange={(e) => handleInputChange("vlpTestCode", e.target.value)}
              fullWidth
              required
              size="small"
              error={!!errors.vlpTestCode}
              helperText={errors.vlpTestCode}
              InputProps={{
                readOnly: isPrefilledMode,
              }}
              sx={{
                ...(isPrefilledMode && {
                  '& .MuiInputBase-input': {
                    bgcolor: 'action.hover',
                  }
                })
              }}
            />
          </Grid>

          {/* Process Test Code - Always editable */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Process Test Code"
              value={formData.processTestCode}
              onChange={(e) =>
                handleInputChange("processTestCode", e.target.value)
              }
              fullWidth
              required
              size="small"
              error={!!errors.processTestCode}
              helperText={errors.processTestCode}
              placeholder={isPrefilledMode ? "Enter process-specific test code" : ""}
            />
          </Grid>

          {/* Parameter - Editable */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Parameter"
              value={formData.parameter}
              onChange={(e) => handleInputChange("parameter", e.target.value)}
              fullWidth
              required
              size="small"
              error={!!errors.parameter}
              helperText={errors.parameter}
            />
          </Grid>

          {/* Method Code - Editable */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Method Code"
              value={formData.methodCode}
              onChange={(e) => handleInputChange("methodCode", e.target.value)}
              fullWidth
              required
              size="small"
              error={!!errors.methodCode}
              helperText={errors.methodCode}
            />
          </Grid>

          {/* Test Units - Editable */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Test Units"
              value={formData.testUnits}
              onChange={(e) => handleInputChange("testUnits", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Min Control Limit - Editable */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Min Control Limit"
              value={formData.minControlLimit}
              onChange={(e) =>
                handleInputChange("minControlLimit", e.target.value)
              }
              fullWidth
              size="small"
            />
          </Grid>

          {/* Max Control Limit - Editable */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Max Control Limit"
              value={formData.maxControlLimit}
              onChange={(e) =>
                handleInputChange("maxControlLimit", e.target.value)
              }
              fullWidth
              size="small"
            />
          </Grid>

          {/* Cost of Analysis - Editable */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Cost of Analysis"
              type="number"
              inputProps={{ step: "0.01" }}
              value={formData.costOfAnalysis}
              onChange={(e) =>
                handleInputChange("costOfAnalysis", e.target.value)
              }
              fullWidth
              size="small"
            />
          </Grid>

          {/* Sort Order */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Sort Order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => handleInputChange("sortOrder", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Sample Location ID */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Sample Location ID"
              type="number"
              value={formData.sampleLocationId}
              onChange={(e) =>
                handleInputChange("sampleLocationId", e.target.value)
              }
              fullWidth
              size="small"
            />
          </Grid>

          {/* Is Limit */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Is Limit</InputLabel>
              <Select
                value={formData.isLimit}
                onChange={(e) => handleInputChange("isLimit", e.target.value)}
                label="Is Limit"
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ pt: 2, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessTestSettingPopup;